import { useMemo, useState } from 'react'
import { abonnerClient, creerClient, initiales } from '../../api/operations'
import {
  abonnementCourant,
  abonnementEnVigueur,
  descriptionConditions,
  estSurDevis,
  libelleDuPlan,
  libelleForfait,
  LIBELLE_STATUT_CLIENT,
  pmeSansAbonnement,
  PASTILLE_STATUT_CLIENT,
} from '../../api/abonnement'
import { jourBamako } from '../../api/encaissement'
import type { Client, Creneau, DonneesOps, Forfait, TypeClient } from '../../api/types'
import { fcfa, JOURS, QUARTIERS } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante, type OptionListe } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { useDroits } from '../Contexte'
import { SelecteurAdresse } from '../SelecteurAdresse'
import { ADRESSE_VIDE, aUnPoint, libelleAdresse, type AdresseSaisie } from '../../api/adresses'
import { LIBELLE_STATUT_JURIDIQUE, STATUTS_JURIDIQUES, statutDe } from '../../api/designationPartie'
import { PanneauClient } from './PanneauClient'
import { CelluleOnboarding, useStatutsOnboarding } from '../onboarding/CelluleOnboarding'

/**
 * Clients — TRANSPOSITION de `renderClients` + `openClientModal` (console HTML,
 * `web/operateur/index.html`). Deux profils de fiche : le particulier « à la
 * course » et la PME « Opérée » (créneaux préenregistrés, supplément hors
 * créneau, plan).
 */

/**
 * ⚠️ ÉCART ASSUMÉ avec la source — le seul de cet écran.
 *
 * La modale d'origine proposait un sélecteur « Mode de facturation » (forfait
 * mensuel / pack prépayé), stocké dans un champ `billing_mode`. Ce champ
 * n'existe ni sur `Client` (types.ts) ni dans `client_accounts` telle que
 * `chargerTout`/`creerClient` la lisent et l'écrivent — la fonctionnalité
 * « pack prépayé » n'a jamais été branchée côté Supabase pour cette console.
 * Le sélecteur écrivait donc une valeur qu'aucun code ne relisait ensuite ; la
 * cellule qui l'affichait retombait TOUJOURS sur `'forfait'||'forfait'`, donc
 * toujours sur le libellé ci-dessous. Le retirer ne change rien à ce qui
 * s'affichait réellement — ça retire seulement un contrôle qui n'aurait eu
 * aucune destination d'écriture dans ce fichier.
 */
function libelleFacturation(c: Client): string {
  return c.type === 'pme' ? 'Forfait mensuel' : 'À la course'
}

export type TypeOuverture = 'particulier' | 'pme'

export function EcranClients({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
  surOuvrirOnboarding,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
  /** Ouvre le dossier d'onboarding d'une PME (écran « Onboarding PME », lot 3). */
  surOuvrirOnboarding?: (clientId: string) => void
}) {
  const [typeModale, setTypeModale] = useState<TypeOuverture | null>(null)
  const [ouvertId, setOuvertId] = useState<string | null>(null)
  const [avecClos, setAvecClos] = useState(false)
  const droits = useDroits()
  const jour = jourBamako()
  const statuts = useStatutsOnboarding(orgId, donnees.tousClients.length)

  /* Les clients clos restent consultables (on peut les réactiver) : `tousClients`, filtré à l'affichage. */
  const liste = useMemo(
    () => donnees.tousClients.filter((c) => avecClos || c.status !== 'closed'),
    [donnees.tousClients, avecClos],
  )
  const nbClos = donnees.tousClients.filter((c) => c.status === 'closed').length
  /* La fiche relit le client dans l'état en direct : un enregistrement s'y voit sans fermer ni rouvrir. */
  const ouvert = ouvertId ? donnees.tousClients.find((c) => c.id === ouvertId) ?? null : null
  const sansAbonnement = donnees.synchro.abonnements
    ? donnees.tousClients.filter((c) => pmeSansAbonnement(c, donnees.abonnements, jour)).length
    : 0

  return (
    <>
      {sansAbonnement > 0 && (
        <div className="bandeau alerte" role="status" style={{ marginBottom: 14 }}>
          <b>
            {sansAbonnement} client{sansAbonnement > 1 ? 's' : ''} PME sans abonnement.
          </b>{' '}
          Leurs courses ne sont pas facturées par la facturation mensuelle : ouvrez la fiche pour souscrire un forfait.
        </div>
      )}
      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <Icone nom="users" classe="h-ic" /> Clients opérés
            </h3>
            <div className="ch-sub">
              {liste.length} client{liste.length > 1 ? 's' : ''} · offre Opérée · cliquez une ligne pour ouvrir la fiche
            </div>
          </div>
          <span className="sp" />
          {nbClos > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 12, fontSize: 12.5, cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={avecClos} onChange={(e) => setAvecClos(e.target.checked)} />
              Afficher les {nbClos} clos
            </label>
          )}
          {droits.peut('clients') && (
            <>
              <button
                className="btn btn-outline btn-sm"
                style={{ marginRight: 8 }}
                onClick={() => setTypeModale('particulier')}
              >
                <Icone nom="plus" /> Particulier
              </button>
              <button className="btn btn-gold btn-sm" onClick={() => setTypeModale('pme')}>
                <Icone nom="plus" /> Onboarder une PME
              </button>
            </>
          )}
        </div>
        <div className="card-body flush">
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Téléphone</th>
                  <th>Forfait</th>
                  <th>Créneaux</th>
                  <th>Suppl. hors créneau</th>
                  <th>Collecte géolocalisée</th>
                  <th>Onboarding</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((c) => {
                  const abo = abonnementEnVigueur(donnees.abonnements, c.id, jour) ?? abonnementCourant(donnees.abonnements, c.id)
                  return (
                    <tr
                      key={c.id}
                      style={{ cursor: 'pointer', opacity: c.status === 'closed' ? 0.6 : undefined }}
                      tabIndex={0}
                      onClick={() => setOuvertId(c.id)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOuvertId(c.id)}
                      aria-label={`Ouvrir la fiche de ${c.name}`}
                    >
                      <td>
                        <div className="u-cell">
                          <span className="avatar">{initiales(c.name)}</span>
                          <div>
                            <div className="t-strong">{c.name}</div>
                            {c.contact_name && <div className="t-muted">{c.contact_name}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${c.type === 'pme' ? 'b-gold' : 'b-blue'} plain`}>
                          {c.type === 'pme' ? 'PME' : 'Particulier'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${PASTILLE_STATUT_CLIENT[c.status]} plain`}>{LIBELLE_STATUT_CLIENT[c.status]}</span>
                      </td>
                      <td className="mono">{c.phone ?? '—'}</td>
                      <td>
                        {libelleFacturation(c)}
                        {c.type === 'pme' && abo && (
                          <div className="t-muted" style={{ fontSize: 11 }}>
                            {libelleDuPlan(abo.plan_code, donnees.forfaits)} · {descriptionConditions(abo, fcfa)}
                          </div>
                        )}
                        {c.type === 'pme' && !abo && c.status === 'active' && donnees.synchro.abonnements && (
                          <div style={{ fontSize: 11, color: 'var(--err)', fontWeight: 600 }}>Sans abonnement</div>
                        )}
                        {c.type === 'pme' && !abo && !donnees.synchro.abonnements && c.plan_code && (
                          <div className="t-muted" style={{ fontSize: 11 }}>{libelleDuPlan(c.plan_code, donnees.forfaits)}</div>
                        )}
                      </td>
                      <td className="t-muted">
                        {c.type === 'pme' ? `${c.slots.length} créneau${c.slots.length > 1 ? 'x' : ''}` : '—'}
                      </td>
                      <td className="mono">{c.type === 'pme' ? fcfa(c.offslot_surcharge_fcfa) : '—'}</td>
                      <td className="t-muted">
                        {c.default_pickup_lat != null ? 'Oui' : 'Non'}
                        {c.option_longue_distance && <div style={{ fontSize: 11 }}>Option longue distance</div>}
                      </td>
                      {/* Le clic sur la cellule d'onboarding ne doit pas ouvrir aussi la fiche client. */}
                      <td onClick={(e) => e.stopPropagation()}>
                        {c.type === 'pme' ? (
                          <CelluleOnboarding statut={statuts.get(c.id)} peutGerer={droits.peut('onboarding_gerer')} surOuvrir={surOuvrirOnboarding && droits.peut('onboarding_voir') ? () => surOuvrirOnboarding(c.id) : undefined} />
                        ) : (
                          <span className="t-muted">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {ouvert && (
        <PanneauClient
          key={ouvert.id}
          client={ouvert}
          donnees={donnees}
          orgId={orgId}
          surFermer={() => setOuvertId(null)}
          surRafraichir={surRafraichir}
          surNotifier={surNotifier}
        />
      )}

      {typeModale && (
        <ModaleClient
          type={typeModale}
          orgId={orgId}
          forfaits={donnees.forfaits}
          supplementDefaut={donnees.tarifs?.supplement_hors_creneau_fcfa ?? 500}
          surFermer={() => setTypeModale(null)}
          surNotifier={surNotifier}
          surCree={async (message, id) => {
            const pme = typeModale === 'pme'
            setTypeModale(null)
            surNotifier(message)
            await surRafraichir()
            /* Une PME naît « prospect » : on ouvre tout de suite son dossier d'onboarding. */
            if (pme && surOuvrirOnboarding && droits.peut('onboarding_voir')) surOuvrirOnboarding(id)
          }}
        />
      )}
    </>
  )
}

/** Statut juridique à la création d'une PME : facultatif, il se complète à l'onboarding (où il devient obligatoire). */
const OPTIONS_STATUT_JURIDIQUE: OptionListe[] = [
  { valeur: '', libelle: 'À préciser à l’onboarding' },
  ...STATUTS_JURIDIQUES.map((j) => ({ valeur: j, libelle: LIBELLE_STATUT_JURIDIQUE[j] })),
]

const OPTIONS_QUARTIER: OptionListe[] = QUARTIERS.map((q) => ({ valeur: q, libelle: q }))

/** Repli si les forfaits de la base sont illisibles (migration 0031 pas poussée) : les trois forfaits de la graine. */
const OPTIONS_PLAN_REPLI: OptionListe[] = [
  { valeur: 'pme_essentiel', libelle: 'PME Essentiel' },
  { valeur: 'pme_business', libelle: 'PME Business' },
  { valeur: 'pme_flotte', libelle: 'PME Sur mesure — sur devis' },
]

/** Jours ISO 1..7 — `JOURS[0]` (chaîne vide, réservée à l'index) est exclu. */
const OPTIONS_JOUR: OptionListe[] = JOURS.slice(1).map((j, i) => ({ valeur: String(i + 1), libelle: j }))

const CRENEAU_PAR_DEFAUT: Creneau = { weekday: 1, start_time: '09:00', end_time: '11:00' }

export function ModaleClient({
  type,
  orgId,
  prerempli = '',
  forfaits = [],
  supplementDefaut = 500,
  surFermer,
  surNotifier,
  surCree,
}: {
  type: TypeOuverture
  /** Ce que l'opératrice a tapé dans « Qui appelle ? » : un numéro va au
      téléphone, un nom au nom — comme `openClientModal(type, prefill)`. */
  prerempli?: string
  orgId: string
  /** Les forfaits de l'organisation (0031) : proposés, avec leur prix et leur quota, à l'onboarding d'une PME. */
  forfaits?: Forfait[]
  /** Supplément hors créneau proposé pour une PME : celui de la grille tarifaire (500 F à défaut). */
  supplementDefaut?: number
  surFermer: () => void
  surNotifier: (message: string) => void
  /** Le message de confirmation, déjà composé (« PME onboardée : … » ou
   * « Client créé : … ») — l'écran l'affiche et rafraîchit. */
  surCree: (message: string, id: string) => Promise<void>
}) {
  const pme = type === 'pme'
  const estNumero = /^[\d+\s]+$/.test(prerempli.trim())
  const nomInitial = estNumero ? '' : prerempli.trim()
  const [nomPme, setNomPme] = useState(pme ? nomInitial : '')
  const [statutJuridique, setStatutJuridique] = useState('')
  const [prenom, setPrenom] = useState(pme ? '' : nomInitial)
  const [nomFamille, setNomFamille] = useState('')
  const [telephone, setTelephone] = useState(estNumero ? prerempli.trim() : '')
  const [quartier, setQuartier] = useState<string>(QUARTIERS[0])
  const droits = useDroits()
  const actifs = forfaits.filter((f) => f.actif)
  const [planCode, setPlanCode] = useState(actifs[0]?.code ?? 'pme_essentiel')
  /* Prix et quota négociés : seulement pour un forfait sur devis, et seulement pour qui a le droit de les fixer. */
  const [prixDevis, setPrixDevis] = useState('')
  const [quotaDevis, setQuotaDevis] = useState('')
  const [supplement, setSupplement] = useState(supplementDefaut)
  /* Collecte habituelle géolocalisée : l'épingle écrit default_pickup_lat/lng, que la prise d'appel reprend. */
  const [collecte, setCollecte] = useState<AdresseSaisie>(ADRESSE_VIDE)
  const [optionLD, setOptionLD] = useState(false)
  const [creneaux, setCreneaux] = useState<Creneau[]>(pme ? [{ ...CRENEAU_PAR_DEFAUT }] : [])
  const [enCours, setEnCours] = useState(false)

  function ajouterCreneau() {
    setCreneaux((c) => [...c, { ...CRENEAU_PAR_DEFAUT }])
  }
  function retirerCreneau(i: number) {
    setCreneaux((c) => c.filter((_, idx) => idx !== i))
  }
  function modifierCreneau(i: number, patch: Partial<Creneau>) {
    setCreneaux((c) => c.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))
  }

  async function enregistrer() {
    const phone = telephone.trim()
    const first = prenom.trim()
    const last = nomFamille.trim()
    let name: string
    let contact_name: string | null = null

    if (pme) {
      name = nomPme.trim()
      if (!name || !first || !last || !phone) {
        surNotifier('Nom du client, interlocuteur (prénom + nom) et téléphone obligatoires')
        return
      }
      contact_name = `${first} ${last}`
    } else {
      if (!first || !last || !phone) {
        surNotifier('Prénom, nom et téléphone obligatoires')
        return
      }
      name = `${first} ${last}`
    }

    /* `particulier` est bien la valeur de l'énumération Postgres
       `client_account_type` (migration 0002 : 'pme' | 'particulier'), et celle
       qu'écrivait déjà la console HTML. Le portage avait d'abord inventé une
       troisième valeur, 'ponctuel', que la base aurait rejetée — corrigée. */
    const type_client: TypeClient = pme ? 'pme' : 'particulier'

    const forfaitChoisi = actifs.find((f) => f.code === planCode)
    const devis = estSurDevis(forfaitChoisi)
    let prixNegocie: number | null = null
    let quotaNegocie: number | null = null
    if (pme && devis && droits.peut('abonnements_devis')) {
      prixNegocie = Number(prixDevis.replace(/[\s  ]/g, ''))
      if (!/^\d+$/.test(prixDevis.replace(/[\s  ]/g, ''))) {
        surNotifier('Forfait sur devis : saisissez le prix mensuel négocié (entier, en francs).')
        return
      }
      if (quotaDevis.trim() !== '') {
        quotaNegocie = Number(quotaDevis.replace(/[\s  ]/g, ''))
        if (!Number.isInteger(quotaNegocie) || quotaNegocie < 1) {
          surNotifier('Le quota est un entier d’au moins 1 (vide = illimité).')
          return
        }
      }
    }

    const c: Omit<Client, 'id' | 'default_pickup_note' | 'email' | 'commune' | 'notes' | 'status' | 'a_un_compte'> = {
      type: type_client,
      name,
      contact_name,
      phone,
      quartier,
      plan_code: pme ? planCode : 'particulier',
      offslot_surcharge_fcfa: pme ? supplement || 0 : 0,
      default_pickup: libelleAdresse(collecte) || quartier,
      default_pickup_lat: aUnPoint(collecte) ? collecte.lat : null,
      default_pickup_lng: aUnPoint(collecte) ? collecte.lng : null,
      option_longue_distance: pme && optionLD,
      slots: pme ? creneaux.filter((s) => s.start_time < s.end_time) : [],
      statut_juridique: pme ? statutDe({ statut_juridique: statutJuridique }) : null,
    }

    setEnCours(true)
    let id: string
    try {
      id = await creerClient(orgId, c)
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
      setEnCours(false)
      return
    }

    /* La PME est créée : on la SOUSCRIT au forfait choisi (prix et quota de la grille figés). Un échec ne défait pas
       la fiche — on le dit : sans abonnement, ses courses ne seraient pas facturées par le mois. Le dispatch ne
       peut souscrire qu'un forfait au prix de la grille ; un sur devis attend le propriétaire ou le comptable. */
    let complement = ''
    if (pme && forfaits.length > 0) {
      if (devis && !droits.peut('abonnements_devis')) {
        complement = ' — forfait sur devis : à souscrire par le propriétaire ou le comptable (fiche du client)'
      } else {
        try {
          await abonnerClient(id, planCode, { prix: prixNegocie, quota: quotaNegocie })
        } catch (e) {
          complement = ` — ATTENTION : abonnement non souscrit (${e instanceof Error ? e.message : String(e)}), à faire depuis la fiche`
        }
      }
    }
    setEnCours(false)
    await surCree(`${pme ? 'PME créée (prospect : onboarding à instruire)' : 'Client créé'} : ${name}${complement}`, id)
  }

  return (
    <Modale
      titre={pme ? 'Onboarder une PME' : 'Nouveau particulier'}
      ouverte
      surFermer={surFermer}
      surEnregistrer={() => void enregistrer()}
      enCours={enCours}
    >
      {pme ? (
        <>
          <div className="field">
            <label>Nom du client (raison sociale ou nom commercial) *</label>
            <input value={nomPme} onChange={(e) => setNomPme(e.target.value)} placeholder="ex. Boutique Lafia" />
          </div>
          <div className="field">
            <label>Statut juridique (facultatif ici, exigé à l’onboarding)</label>
            <ListeDeroulante valeur={statutJuridique} options={OPTIONS_STATUT_JURIDIQUE} surChangement={setStatutJuridique} pleineLargeur />
          </div>
          <div className="grid-2" style={{ gap: 12 }}>
            <div className="field">
              <label>Interlocuteur — Prénom *</label>
              <input value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="ex. Mariam" />
            </div>
            <div className="field">
              <label>Interlocuteur — Nom *</label>
              <input value={nomFamille} onChange={(e) => setNomFamille(e.target.value)} placeholder="ex. Sangaré" />
            </div>
          </div>
        </>
      ) : (
        <div className="grid-2" style={{ gap: 12 }}>
          <div className="field">
            <label>Prénom *</label>
            <input value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="ex. Aïcha" />
          </div>
          <div className="field">
            <label>Nom *</label>
            <input value={nomFamille} onChange={(e) => setNomFamille(e.target.value)} placeholder="ex. Diallo" />
          </div>
        </div>
      )}

      <div className="field">
        <label>Téléphone *</label>
        <input value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="+223 …" />
      </div>

      <div className="field">
        <label>Quartier (collecte habituelle)</label>
        <ListeDeroulante valeur={quartier} options={OPTIONS_QUARTIER} surChangement={setQuartier} pleineLargeur />
      </div>

      <SelecteurAdresse
        titre="Collecte habituelle (épingle)"
        valeur={collecte}
        surChange={setCollecte}
        libelleConnues=""
        placeholderTexte="ex. Boutique Lafia, marché de Lafiabougou"
        placeholderRepere="ex. face à la pharmacie, portail bleu"
      />

      {pme && (
        <>
          <div className="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={optionLD} onChange={(e) => setOptionLD(e.target.checked)} />
              Option « longue distance » (km supplémentaires au tarif réduit)
            </label>
            <div className="t-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              Abonnement mensuel ; le tarif au km réduit s’applique au-delà du seuil de longue distance (réglable dans « Réglages »).
            </div>
          </div>
          <div className="field">
            <label>Forfait / plan</label>
            <ListeDeroulante
              valeur={planCode}
              options={actifs.length > 0 ? actifs.map((f) => ({ valeur: f.code, libelle: libelleForfait(f, fcfa) })) : OPTIONS_PLAN_REPLI}
              surChangement={setPlanCode}
              pleineLargeur
            />
            <div className="t-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              {actifs.length > 0
                ? 'Le prix et le quota de la grille sont FIGÉS à la souscription : changer la grille plus tard ne touche pas ce client. Le mois de début est facturé en entier.'
                : 'Forfaits de la grille illisibles (mise à jour de la base à appliquer) : le client est créé sans abonnement.'}
            </div>
          </div>
          {actifs.length > 0 && estSurDevis(actifs.find((f) => f.code === planCode)) && (
            droits.peut('abonnements_devis') ? (
              <div className="grid-2" style={{ gap: 12 }}>
                <div className="field">
                  <label>Prix mensuel négocié (F TTC) *</label>
                  <input inputMode="numeric" value={prixDevis} onChange={(e) => setPrixDevis(e.target.value)} />
                </div>
                <div className="field">
                  <label>Quota de courses (vide = illimité)</label>
                  <input inputMode="numeric" value={quotaDevis} onChange={(e) => setQuotaDevis(e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="bandeau alerte" style={{ marginBottom: 12 }}>
                Un forfait sur devis (prix négocié) est fixé par le propriétaire ou le comptable : la PME sera créée sans abonnement, à souscrire ensuite depuis sa fiche.
              </div>
            )
          )}
          <div className="field">
            <label>Supplément hors créneau (F)</label>
            <input
              type="number"
              min={0}
              step={250}
              value={supplement}
              onChange={(e) => setSupplement(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="field">
            <label>Créneaux préenregistrés (livreur auto-assigné dedans)</label>
            <div className="slot-rows">
              {creneaux.map((s, i) => (
                <div className="slot-row" key={i}>
                  <ListeDeroulante
                    valeur={String(s.weekday)}
                    options={OPTIONS_JOUR}
                    surChangement={(v) => modifierCreneau(i, { weekday: parseInt(v, 10) })}
                  />
                  <input
                    type="time"
                    value={s.start_time}
                    onChange={(e) => modifierCreneau(i, { start_time: e.target.value })}
                  />
                  <input
                    type="time"
                    value={s.end_time}
                    onChange={(e) => modifierCreneau(i, { end_time: e.target.value })}
                  />
                  <button className="rm" type="button" title="Retirer" onClick={() => retirerCreneau(i)}>
                    <Icone nom="x" />
                  </button>
                </div>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} type="button" onClick={ajouterCreneau}>
              <Icone nom="plus" /> Ajouter un créneau
            </button>
          </div>
        </>
      )}
    </Modale>
  )
}
